import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { userGoogleLogin } from "../../Action/api.js";
import { GoogleLogin } from "@react-oauth/google";
import CLogo from "../../Components/Reusable/Logo";
// import PhoneNumberDialog from "../../Components/PhoneNumberDialog";
import { decodeJWT } from "../../Utils";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CampaignIcon from "@mui/icons-material/Campaign";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SecurityIcon from "@mui/icons-material/Security";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

// ─── loginApp resolution helper ──────────────────────────────────────────────
// The token's top-level "loginApp" node is the single source of truth for
// where this login should redirect. Its own "url" field already encodes the
// live target; an optional local-dev override (seen under backUp/bk/backup
// on other app nodes, key name not yet standardized by the backend) is only
// honored when explicitly opted in — this app's own dev/prod mode says
// nothing about whether the *target* app is running locally too.
// Deliberately NOT read from userData — that copy is deprecated/stale.
const pickAppUrl = (appEntry) => {
  if (!appEntry) return null;
  const useLocal = process.env.REACT_APP_USE_LOCAL_APP_URLS === "true";
  const localUrl = appEntry.backUp || appEntry.bk || appEntry.backup;
  if (useLocal && localUrl) return localUrl;
  return appEntry.url || null;
};

const getLoginAppUrl = (decodedToken) => pickAppUrl(decodedToken?.loginApp);

// CloudFront (and most CDNs/browsers) reject request URIs beyond ~8,192 bytes
// with a 414. The auth token is large enough on its own that it's worth
// failing loudly and specifically here rather than sending the browser into
// a CDN error page with no useful signal.
const MAX_REDIRECT_URL_LENGTH = 8100;

class AuthRedirectError extends Error {
  constructor(message) {
    super(message);
    this.name = "AuthRedirectError";
  }
}

// ─── Auth helper ──────────────────────────────────────────────────────────────
const storeAuthData = (data) => {
  const loginTime = Date.now().toString();
  localStorage.setItem("authToken", data.authorizationToken);
  localStorage.setItem("isAuthenticated", "true");
  localStorage.setItem("loginTime", loginTime);

  const decodedToken = decodeJWT(data.authorizationToken);
  const loginAppUrl = getLoginAppUrl(decodedToken);
  if (!loginAppUrl) return null;

  // localStorage is per-origin, so it doesn't carry over to the destination
  // app's port. Pass the token via URL so that app can pick it up itself.
  const urlObj = new URL(loginAppUrl);
  urlObj.searchParams.set("userToken", data.authorizationToken);
  urlObj.searchParams.set("isAuthenticated", "true");
  urlObj.searchParams.set("loginTime", loginTime);

  const target = urlObj.toString();
  if (target.length > MAX_REDIRECT_URL_LENGTH) {
    console.error(
      `Auth redirect URL is ${target.length} chars (limit ${MAX_REDIRECT_URL_LENGTH}) — ` +
        "the auth token itself is too large. Check the backend for duplicated " +
        "or unnecessary fields in the token payload."
    );
    throw new AuthRedirectError(
      "Your session data is too large to redirect. Please contact support."
    );
  }
  return target;
};

// ─── TEMP DEBUG: handoff preview ─────────────────────────────────────────────
// Shows the computed redirect URL + full decoded token before navigating away
// so the handshake with the destination app can be sanity-checked by hand.
// Off by default — a dev who wants it sets REACT_APP_SHOW_AUTH_DEBUG=true in
// a local .env.local (never committed, so it can't leak into a real build).
// Delete this block and its two usages below (search "TEMP DEBUG") once the
// handoff no longer needs manual verification.
const SHOW_AUTH_DEBUG_MODAL = process.env.REACT_APP_SHOW_AUTH_DEBUG === "true";

const deriveAppLabel = (urlString) => {
  try {
    const host = new URL(urlString).hostname;
    const skip = new Set(["www", "app", "oruclick", "oruclicks", "com"]);
    const label = host.split(".").find((part) => !skip.has(part)) || host;
    return label.charAt(0).toUpperCase() + label.slice(1);
  } catch {
    return "App";
  }
};
// ─── END TEMP DEBUG ───────────────────────────────────────────────────────────

// ─── Source validation helper ────────────────────────────────────────────────
// Always re-reads window.location directly (never trusts cached state) so a
// URL edited/replaced without a full remount can't leave a stale valid source
// in place.
const getSourceFromUrl = () => new URLSearchParams(window.location.search).get("source");

// ─── Feature list for the left panel ─────────────────────────────────────────
const features = [
  {
    icon: <ScheduleIcon sx={{ fontSize: 20 }} />,
    title: "Smart Scheduling",
    desc: "Schedule posts across all platforms in one click",
  },
  {
    icon: <CampaignIcon sx={{ fontSize: 20 }} />,
    title: "Multi-Channel Campaigns",
    desc: "Email, SMS & social media from a single dashboard",
  },
  {
    icon: <TrendingUpIcon sx={{ fontSize: 20 }} />,
    title: "Analytics & Insights",
    desc: "Real-time performance tracking to grow faster",
  },
  {
    icon: <AutoAwesomeIcon sx={{ fontSize: 20 }} />,
    title: "AI-Powered Content",
    desc: "Generate captions, posts and campaigns with AI",
  },
];

const stats = [
  { value: "10k+", label: "Businesses" },
  { value: "50M+", label: "Posts Sent" },
  { value: "99.9%", label: "Uptime" },
];

// ─── peopleSoftware role → dashboard route mapping ───────────────────────────
const roleRoutes = {
  "AUJ-GROUP-001": "/founder-dashboard", // Chief Product Officer
  "U-PT-ROLE-001": "/cpo-dashboard", // Chief Product Officer
  "U-PT-ROLE-002": "/product-manager-dashboard", // Product Manager
  "U-PT-ROLE-003": "/associate-product-manager-dashboard", // Associate Product Manager
  "U-PT-ROLE-004": "/junior-product-manager-dashboard", // Junior Product Manager
  "U-PT-ROLE-005": "/product-intern-dashboard", // Product Intern / Trainee
  "U-MG-ROLE-001": "/cmo-dashboard", // Chief Marketing Officer
  "U-MG-ROLE-002": "/growth-marketing-dashboard", // Growth Marketing Manager
  "U-MG-ROLE-003": "/content-strategist-dashboard", // Content Strategist
  // "U-MG-ROLE-004": "/social-media-dashboard", // Social Media Executive
  "U-SB-ROLE-001": "/vp-sales-dashboard", // VP of Sales
  "U-SB-ROLE-002": "/sales-manager-dashboard", // Sales Manager
  "U-SB-ROLE-003": "/business-development-dashboard", // Business Development Executive
  // "U-SB-ROLE-004": "/executive-trainee-dashboard", // Executive Trainee
  "U-HR-ROLE-001": "/executive-hr-dashboard", // Executive Trainee
  "U-HR-ROLE-002": "/senior-trainee-dashboard", // Executive Trainee
  "U-HR-ROLE-003": "/mid-senior-trainee-dashboard", // Executive Trainee
  "U-HR-ROLE-004": "/junior-trainee-dashboard", // Executive Trainee
};

// ─── Main Login component ─────────────────────────────────────────────────────
export const Login = () => {
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState("");
  const [btnWidth, setBtnWidth] = useState(300);
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);
  const [pendingCredential, setPendingCredential] = useState(null);
  const [source, setSource] = useState(null);
  const [sourceChecked, setSourceChecked] = useState(false);
  // TEMP DEBUG: see SHOW_AUTH_DEBUG_MODAL above
  const [debugHandoff, setDebugHandoff] = useState(null);
  const [debugUrlCopied, setDebugUrlCopied] = useState(false);
  const [debugTokenCopied, setDebugTokenCopied] = useState(false);
  const btnContainerRef = useRef(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    const measure = () => {
      if (btnContainerRef.current) {
        setBtnWidth(btnContainerRef.current.offsetWidth);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    const checkSource = () => {
      setSource(getSourceFromUrl());
      setSourceChecked(true);
    };
    checkSource();
    // Covers browser back/forward navigation, which doesn't remount the component.
    window.addEventListener("popstate", checkSource);
    return () => window.removeEventListener("popstate", checkSource);
  }, []);

  const isUnknownSource = sourceChecked && !source;

  const sourceBanner = isUnknownSource && (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 2000,
      }}
    >
      <Alert severity="error" sx={{ borderRadius: 0, justifyContent: "center", fontWeight: 600 }}>
        Access denied: this link is missing a valid source. Please use the correct sign-in link.
      </Alert>
    </Box>
  );

  const handleGoogle = async (credentialResponse) => {
    // Re-read from the URL directly rather than trusting component state,
    // so a URL changed after mount can't be missed.
    const currentSource = getSourceFromUrl();
    if (!currentSource) {
      setSource(null);
      setSourceChecked(true);
      setError("Unknown source. Login is not allowed from this link.");
      return;
    }
    setLoading(true);
    setError("");

     const googleCredential = credentialResponse?.credential;
      if (!googleCredential) {
        setError("Google authentication failed. Missing credential token.");
        setLoading(false);
        return;
      }

    try {
      // First attempt login without phone number
      const response = await userGoogleLogin(googleCredential, null, currentSource);
      
      // Debug: Log the full response to see what backend returns
      console.log("Login API Response:", response);
      console.log("Response data:", response?.data);

      // peopleSoftware has its own response shape and redirect rules —
      // handle it separately from the mainSoftware/marketingSotware flow below.
      if (currentSource === "peopleSoftware") {
        if (!response.data || response.isAxiosError || !response.data.status) {
          setError(response.data?.uiMsg || "Google login failed. Please try again.");
          setLoading(false);
          return;
        }

        const responseData = response.data;
        const userData = responseData.userData;

        if (!userData) {
          setError("Login failed. Please try again.");
          setLoading(false);
          return;
        }

        // Check if user is not activated (staffStatus is Unknown)
        if (userData.staffStatus === "Unknown") {
          setError("User ID is unauthorized. Please check with the Orunal team to get activated.");
          setLoading(false);
          return;
        }

        const roleId = userData.roleId || "";
        const appAccess = userData.appAccess || [];

        // Store authorization token
        localStorage.setItem("authToken", responseData.authorizationToken || "");
        localStorage.setItem("isAuthenticated", "true");
        localStorage.setItem("loginTime", Date.now());

        // Find main app and redirect
        let mainAppUrl = null;
        for (const appItem of appAccess) {
          const appKey = Object.keys(appItem)[0];
          const appData = appItem[appKey];
          if (appData.mainApp === true && appData.permission === true && appData.url) {
            mainAppUrl = appData.url;
            break;
          }
        }

        if (mainAppUrl) {
          setRedirecting(true);
          const urlObj = new URL(mainAppUrl);
          urlObj.searchParams.set("userToken", responseData.authorizationToken);
          setTimeout(() => {
            window.location.href = urlObj.toString();
          }, 800);
        } else {
          const route = roleRoutes[roleId];
          navigate(route || "/task-dashboard");
        }
        return;
      }

      // Decode the JWT token to get isNewUser field
      const token = response?.data?.authorizationToken;
      if (token) {
        const decodedToken = decodeJWT(token);
        console.log("Decoded token payload:", decodedToken);
        console.log("Is new user flag from token:", decodedToken?.isNewUser);
        
        // Backend sends "Yes" or "No" as string inside the token
        const isNewUser = decodedToken?.isNewUser === "Yes";
        
        console.log("Determined isNewUser:", isNewUser);
        
        // Show dialog ONLY for explicitly marked new users
        if (isNewUser === true) {
          // Store the credential for later use with phone number
          setPendingCredential(credentialResponse.credential);
          setPhoneDialogOpen(true);
          setLoading(false);
          console.log("Opening phone dialog for NEW user signup");
          return;
        }
      }
      
      // Existing user flow OR new user without phone requirement - proceed with navigation
      if (response?.data?.status) {
        console.log("Proceeding to dashboard (existing user or completed signup)");
        let target;
        if (currentSource === "crmSoftware") {
          const crmUrl = new URL("https://www.crm.app.oruclick.com/");
          // const crmUrl = new URL("http://localhost:5013/");
          crmUrl.searchParams.set("userToken", response.data.authorizationToken);
          target = crmUrl.toString();
        } else {
          target = storeAuthData(response.data);
        }

        if (!target) {
          setError("No active app found for this account. Please contact support.");
          setLoading(false);
          return;
        }

        // TEMP DEBUG: see SHOW_AUTH_DEBUG_MODAL above
        if (SHOW_AUTH_DEBUG_MODAL) {
          setDebugHandoff({
            target,
            token: response.data.authorizationToken,
            decodedToken: decodeJWT(response.data.authorizationToken),
            appLabel: deriveAppLabel(target),
          });
          setLoading(false);
          return;
        }

        setRedirecting(true);
        setTimeout(() => {
          window.location.href = target;
        }, 900);
        return;
      } else {
        setError(
          response?.data?.uiMsg ||
            response?.response?.data?.uiMsg ||
            "Authentication failed. Please try again."
        );
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(err.name === "AuthRedirectError" ? err.message : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // TEMP DEBUG: see SHOW_AUTH_DEBUG_MODAL above
  const handleDebugClose = () => {
    setDebugHandoff(null);
    setDebugUrlCopied(false);
    setDebugTokenCopied(false);
  };
  const handleDebugCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(debugHandoff.target);
      setDebugUrlCopied(true);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };
  const handleDebugCopyToken = async () => {
    try {
      await navigator.clipboard.writeText(debugHandoff.token);
      setDebugTokenCopied(true);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };
  const handleDebugOpenApp = () => {
    const { target } = debugHandoff;
    setDebugHandoff(null);
    setRedirecting(true);
    setTimeout(() => {
      window.location.href = target;
    }, 900);
  };
  const debugHandoffModal = (
    <Dialog open={!!debugHandoff} onClose={handleDebugClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Debug handoff: {debugHandoff?.appLabel}
      </DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#6b7280", mb: 1 }}>
          URL (with token)
        </Typography>
        <Box
          sx={{
            fontFamily: "monospace",
            fontSize: "0.72rem",
            background: "#f8f9fc",
            border: "1px solid #e5e7eb",
            borderRadius: 1.5,
            p: 1.5,
            mb: 2.5,
            maxHeight: 160,
            overflow: "auto",
            wordBreak: "break-all",
          }}
        >
          {debugHandoff?.target}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#6b7280" }}>
            Token (extracted)
          </Typography>
          <Button
            size="small"
            onClick={handleDebugCopyToken}
            sx={{ minWidth: 0, py: 0, fontSize: "0.7rem", textTransform: "none" }}
          >
            {debugTokenCopied ? "Copied!" : "Copy token"}
          </Button>
        </Box>
        <Box
          sx={{
            fontFamily: "monospace",
            fontSize: "0.72rem",
            background: "#f8f9fc",
            border: "1px solid #e5e7eb",
            borderRadius: 1.5,
            p: 1.5,
            mb: 2.5,
            maxHeight: 120,
            overflow: "auto",
            wordBreak: "break-all",
          }}
        >
          {debugHandoff?.token}
        </Box>

        <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#6b7280", mb: 1 }}>
          Decoded token payload — the full thing being sent, not just loginApp
        </Typography>
        <Box
          component="pre"
          sx={{
            fontFamily: "monospace",
            fontSize: "0.72rem",
            background: "#f8f9fc",
            border: "1px solid #e5e7eb",
            borderRadius: 1.5,
            p: 1.5,
            maxHeight: 240,
            overflow: "auto",
            m: 0,
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
          }}
        >
          {JSON.stringify(debugHandoff?.decodedToken, null, 2)}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleDebugClose} color="inherit">
          Close
        </Button>
        <Button onClick={handleDebugCopyUrl} variant="outlined">
          {debugUrlCopied ? "Copied!" : "Copy URL"}
        </Button>
        <Button onClick={handleDebugOpenApp} variant="contained">
          Open App
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ── Post-auth redirect loader ───────────────────────────────────────────
  if (redirecting) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 3.5,
          background: "linear-gradient(135deg, #ffffff 0%, #f2f0ff 100%)",
        }}
      >
        <CLogo size="56px" textSize="1.7rem" isDarkTheme={false} />

        <Box sx={{ display: "flex", gap: "7px" }}>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: "#4F46E5",
                animation: "dotBounce 1.2s ease-in-out infinite",
                animationDelay: `${i * 0.15}s`,
                "@keyframes dotBounce": {
                  "0%, 80%, 100%": { transform: "scale(0.7)", opacity: 0.4 },
                  "40%": { transform: "scale(1)", opacity: 1 },
                },
              }}
            />
          ))}
        </Box>

        <Typography sx={{ color: "#94a3b8", fontSize: "0.9rem", fontWeight: 500 }}>
          Setting up your workspace…
        </Typography>
      </Box>
    );
  }

  // ── Mobile layout ────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "#fff" }}>
        {sourceBanner}
        {debugHandoffModal}

        {/* Mobile gradient header strip */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #0f0c29 0%, #302b63 60%, #24243e 100%)",
            px: 3,
            pt: 3,
            pb: 4,
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* blob */}
          <Box sx={{ position: "absolute", width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle, rgba(79,70,229,0.25) 0%, transparent 70%)", top: -60, right: -60, pointerEvents: "none" }} />

          {/* back link */}
          <Box
            onClick={() => navigate("/")}
            sx={{ display: "flex", alignItems: "center", gap: 0.6, color: "rgba(255,255,255,0.55)", cursor: "pointer", mb: 3, fontSize: "0.82rem", transition: "color 0.2s", "&:hover": { color: "#fff" }, userSelect: "none", width: "fit-content" }}
          >
            <ArrowBackIcon sx={{ fontSize: 15 }} />
            Back to home
          </Box>

          {/* brand */}
          <Box sx={{ mb: 1.5 }}>
            <CLogo size="42px" textSize="1.2rem" isDarkTheme={true} />
          </Box>

          <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.8rem", lineHeight: 1.6 }}>
            Schedule, publish &amp; grow — all from one workspace.
          </Typography>
        </Box>

        {/* Mobile form area — scrollable */}
        <Box sx={{ flex: 1, overflowY: "auto", px: 3, pt: 4, pb: 5, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Box sx={{ width: "100%", maxWidth: 400 }}>

            {/* Get Started badge */}
            <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
              <Box sx={{
                display: "inline-flex", alignItems: "center", gap: 0.75,
                py: "8px", px: 2.5, borderRadius: "999px",
                background: "linear-gradient(135deg, #4F46E5 0%, #0891B2 100%)",
                boxShadow: "0 4px 14px rgba(79,70,229,0.45)",
                userSelect: "none",
              }}>
                <AutoAwesomeIcon sx={{ fontSize: 15, color: "#fff" }} />
                <Typography sx={{ fontWeight: 800, fontSize: "0.85rem", color: "#fff", letterSpacing: "0.04em" }}>
                  GET STARTED
                </Typography>
              </Box>
            </Box>

            {/* Heading */}
            <Typography sx={{ fontWeight: 800, fontSize: "1.4rem", color: "#0f172a", mb: 0.75, letterSpacing: "-0.025em", lineHeight: 1.25 }}>
              Welcome 👋
            </Typography>
            <Typography sx={{ color: "#6b7280", fontSize: "0.85rem", lineHeight: 1.7, mb: 3.5 }}>
              New or returning — just continue with Google and you're in.{" "}
              <Box component="span" sx={{ color: "#4F46E5", fontWeight: 700 }}>No password needed.</Box>
            </Typography>

            {/* Trust badges */}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3.5 }}>
              <Chip
                icon={<SecurityIcon sx={{ fontSize: "13px !important" }} />}
                label="Secure & Encrypted"
                size="small"
                sx={{ background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0", fontWeight: 600, fontSize: "0.7rem", height: 26, "& .MuiChip-icon": { color: "#15803d" } }}
              />
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: "13px !important" }} />}
                label="No credit card"
                size="small"
                sx={{ background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", fontWeight: 600, fontSize: "0.7rem", height: 26, "& .MuiChip-icon": { color: "#1d4ed8" } }}
              />
            </Box>

            {/* Error */}
            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

            {/* Google button */}
            {!isUnknownSource && (
              <Box sx={{ border: "1.5px solid #e5e7eb", borderRadius: "14px", p: "14px", background: "#fafafa", mb: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.04)", transition: "border-color 0.2s, box-shadow 0.2s", "&:hover": { borderColor: "#c7d2fe", boxShadow: "0 4px 14px rgba(79,70,229,0.12)" } }} ref={btnContainerRef}>
                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
                    <CircularProgress size={28} sx={{ color: "#4F46E5" }} />
                  </Box>
                ) : (
                  <GoogleLogin
                    onSuccess={handleGoogle}
                    onError={() => setError("Google authentication failed. Please try again.")}
                    theme="outline"
                    size="large"
                    width={String(btnWidth - 28)}
                    text="continue_with"
                    shape="rectangular"
                    logo_alignment="center"
                  />
                )}
              </Box>
            )}

            {/* OR divider */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
              <Box sx={{ flex: 1, height: "1px", background: "#f0f0f0" }} />
              <Typography sx={{ color: "#d1d5db", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.05em" }}>OR</Typography>
              <Box sx={{ flex: 1, height: "1px", background: "#f0f0f0" }} />
            </Box>

            {/* More options pill */}
            <Box sx={{ textAlign: "center", py: 1.5, borderRadius: "10px", border: "1.5px dashed #e5e7eb", color: "#9ca3af", fontSize: "0.78rem", mb: 4, userSelect: "none" }}>
              More sign-in options coming soon
            </Box>

            {/* Legal */}
            <Typography sx={{ display: "block", textAlign: "center", color: "#9ca3af", lineHeight: 1.85, fontSize: "0.72rem" }}>
              New users get an account automatically.
              <br />
              By continuing you agree to our{" "}
              <Box component="span" onClick={() => window.open("https://www.oruclicks.com/termsandconditions", "_blank", "noopener,noreferrer")} sx={{ color: "#4F46E5", cursor: "pointer", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}>Terms of Service</Box>{" "}
              &amp;{" "}
              <Box component="span" onClick={() => window.open("https://www.oruclicks.com/privacy-policy", "_blank", "noopener,noreferrer")} sx={{ color: "#4F46E5", cursor: "pointer", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}>Privacy Policy</Box>.
            </Typography>

          </Box>
        </Box>

        {/* Phone Number Dialog */}
        {/* <PhoneNumberDialog
          open={phoneDialogOpen}
          onSubmit={handlePhoneNumberSubmit}
          onClose={handlePhoneDialogClose}
          loading={loading}
        /> */}
      </Box>
    );
  }

  // ── Desktop layout ────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "row",
        background: "#f8f9fc",
        overflow: "hidden",
      }}
    >
      {sourceBanner}
      {debugHandoffModal}

      {/* ── LEFT PANEL ───────────────────────────────────────────────────────── */}
      {(
        <Box
          sx={{
            flex: "0 0 55%",
            background: "linear-gradient(145deg, #0f0c29 0%, #302b63 45%, #24243e 100%)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
            p: { md: 7, lg: 10 },
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Decorative blurred circles */}
          <Box
            sx={{
              position: "absolute",
              width: 420,
              height: 420,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(79,70,229,0.22) 0%, transparent 70%)",
              top: -120,
              right: -120,
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: 300,
              height: 300,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(5,150,105,0.28) 0%, transparent 70%)",
              bottom: 40,
              left: -90,
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: 180,
              height: 180,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(103,232,249,0.12) 0%, transparent 70%)",
              bottom: "40%",
              right: "8%",
              pointerEvents: "none",
            }}
          />

          {/* Back to home */}
          <Box
            onClick={() => navigate("/")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              color: "rgba(255,255,255,0.5)",
              cursor: "pointer",
              mb: 7,
              fontSize: "0.85rem",
              transition: "color 0.2s",
              "&:hover": { color: "rgba(255,255,255,0.9)" },
              userSelect: "none",
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 16 }} />
            Back to home
          </Box>

          {/* Brand mark */}
          <Box sx={{ mb: 4 }}>
            <CLogo size="48px" textSize="1.45rem" isDarkTheme={true} />
          </Box>

          {/* Headline */}
          <Typography
            sx={{
              color: "#fff",
              fontWeight: 800,
              fontSize: { md: "2rem", lg: "2.6rem" },
              lineHeight: 1.2,
              letterSpacing: "-0.03em",
              mb: 2,
              maxWidth: 480,
            }}
          >
            Your all-in-one
            <Box
              component="span"
              sx={{
                display: "block",
                background: "linear-gradient(90deg, #818CF8, #22D3EE)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Marketing Platform
            </Box>
          </Typography>

          <Typography
            sx={{
              color: "rgba(255,255,255,0.55)",
              fontSize: "0.95rem",
              lineHeight: 1.75,
              mb: 5,
              maxWidth: 420,
            }}
          >
            Schedule, publish, and analyse your content across every channel — all from one powerful workspace.
          </Typography>

          {/* Feature list */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
              mb: 6,
              width: "100%",
              maxWidth: 440,
            }}
          >
            {features.map((f) => (
              <Box key={f.title} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <Box
                  sx={{
                    mt: 0.1,
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    background: "rgba(255,255,255,0.07)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#818CF8",
                    flexShrink: 0,
                  }}
                >
                  {f.icon}
                </Box>
                <Box>
                  <Typography
                    sx={{ color: "#fff", fontWeight: 700, fontSize: "0.88rem", lineHeight: 1.3 }}
                  >
                    {f.title}
                  </Typography>
                  <Typography
                    sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.78rem", lineHeight: 1.5 }}
                  >
                    {f.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Divider */}
          <Box sx={{ width: "100%", maxWidth: 440, height: "1px", background: "rgba(255,255,255,0.08)", mb: 4 }} />

          {/* Stats */}
          <Box sx={{ display: "flex", gap: 5 }}>
            {stats.map((s) => (
              <Box key={s.label}>
                <Typography
                  sx={{ color: "#818CF8", fontWeight: 800, fontSize: "1.5rem", lineHeight: 1.1 }}
                >
                  {s.value}
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.75rem", mt: 0.3 }}>
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* ── RIGHT PANEL ──────────────────────────────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          p: { md: 6, lg: 8 },
          background: "#fff",
          position: "relative",
        }}
      >
        {/* Subtle blobs */}
        <Box sx={{ position: "absolute", top: -70, right: -70, width: 240, height: 240, borderRadius: "50%", background: "radial-gradient(circle, rgba(79,70,229,0.07) 0%, transparent 70%)", pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", bottom: -50, left: -50, width: 180, height: 180, borderRadius: "50%", background: "radial-gradient(circle, rgba(5,150,105,0.06) 0%, transparent 70%)", pointerEvents: "none" }} />

        <Box sx={{ width: "100%", maxWidth: 400, position: "relative", zIndex: 1 }}>

          {/* Get Started badge */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 5 }}>
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: 0.75,
              py: "8px", px: 2.5, borderRadius: "999px",
              background: "linear-gradient(135deg, #4F46E5 0%, #0891B2 100%)",
              boxShadow: "0 4px 14px rgba(79,70,229,0.45)",
              userSelect: "none",
            }}>
              <AutoAwesomeIcon sx={{ fontSize: 15, color: "#fff" }} />
              <Typography sx={{ fontWeight: 800, fontSize: "0.85rem", color: "#fff", letterSpacing: "0.04em" }}>
                GET STARTED
              </Typography>
            </Box>
          </Box>

          {/* Heading */}
          <Typography sx={{ fontWeight: 800, fontSize: "1.6rem", color: "#0f172a", mb: 0.75, letterSpacing: "-0.025em", lineHeight: 1.25 }}>
            Welcome 👋
          </Typography>
          <Typography sx={{ color: "#6b7280", fontSize: "0.88rem", lineHeight: 1.7, mb: 4 }}>
            New or returning — just continue with Google and you're in.{" "}
            <Box component="span" sx={{ color: "#4F46E5", fontWeight: 700 }}>No password needed.</Box>
          </Typography>

          {/* Trust badges */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 4 }}>
            <Chip icon={<SecurityIcon sx={{ fontSize: "13px !important" }} />} label="Secure & Encrypted" size="small" sx={{ background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0", fontWeight: 600, fontSize: "0.7rem", height: 26, "& .MuiChip-icon": { color: "#15803d" } }} />
            <Chip icon={<CheckCircleIcon sx={{ fontSize: "13px !important" }} />} label="No credit card" size="small" sx={{ background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", fontWeight: 600, fontSize: "0.7rem", height: 26, "& .MuiChip-icon": { color: "#1d4ed8" } }} />
          </Box>

          {/* Error */}
          {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

          {/* Google button */}
          {!isUnknownSource && (
            <Box sx={{ border: "1.5px solid #e5e7eb", borderRadius: "14px", p: "14px", background: "#fafafa", mb: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.04)", transition: "border-color 0.2s, box-shadow 0.2s", "&:hover": { borderColor: "#c7d2fe", boxShadow: "0 4px 14px rgba(79,70,229,0.12)" } }} ref={btnContainerRef}>
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
                  <CircularProgress size={28} sx={{ color: "#4F46E5" }} />
                </Box>
              ) : (
                <GoogleLogin
                  onSuccess={handleGoogle}
                  onError={() => setError("Google authentication failed. Please try again.")}
                  theme="outline"
                  size="large"
                  width={String(btnWidth - 28)}
                  text="continue_with"
                  shape="rectangular"
                  logo_alignment="center"
                />
              )}
            </Box>
          )}

          {/* OR divider */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
            <Box sx={{ flex: 1, height: "1px", background: "#f0f0f0" }} />
            <Typography sx={{ color: "#d1d5db", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.05em" }}>OR</Typography>
            <Box sx={{ flex: 1, height: "1px", background: "#f0f0f0" }} />
          </Box>

          {/* More options pill */}
          <Box sx={{ textAlign: "center", py: 1.5, borderRadius: "10px", border: "1.5px dashed #e5e7eb", color: "#9ca3af", fontSize: "0.78rem", mb: 5, userSelect: "none", letterSpacing: "0.01em" }}>
            More sign-in options coming soon
          </Box>

          {/* Legal */}
          <Typography sx={{ display: "block", textAlign: "center", color: "#9ca3af", lineHeight: 1.85, fontSize: "0.72rem" }}>
            New users get an account automatically.
            <br />
            By continuing you agree to our{" "}
            <Box component="span" onClick={() => window.open("https://www.oruclicks.com/termsandconditions", "_blank", "noopener,noreferrer")} sx={{ color: "#4F46E5", cursor: "pointer", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}>Terms of Service</Box>{" "}
            &amp;{" "}
            <Box component="span" onClick={() => window.open("https://www.oruclicks.com/privacy-policy", "_blank", "noopener,noreferrer")} sx={{ color: "#4F46E5", cursor: "pointer", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}>Privacy Policy</Box>.
          </Typography>

        </Box>
      </Box>

      {/* Phone Number Dialog */}
      {/* <PhoneNumberDialog
        open={phoneDialogOpen}
        onSubmit={handlePhoneNumberSubmit}
        onClose={handlePhoneDialogClose}
        loading={loading}
      /> */}
    </Box>
  );
};

export default Login;
